// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWizard, type WizardClient, deriveProfileName } from '../WizardContext';
import { relationshipBulkApi } from '../../../api/relationshipBulkApi';
import { clientApi, type EmailAvailability } from '../../../api/clientApi';
import { DEFAULT_COUNTRY, SUPPORTED_COUNTRIES, normalizePostalCode, getCountryConfig, validatePostalCode } from '../../../config/addressConfig';
import type { RelationshipType } from '../../../types/relationship';
import type { RelationshipBulkCreateDTO, ValidationErrorsDTO } from '../../../types/bulk';
import { InputLabel, DateTimeInput, Dropdown, useToast } from '@loa/ui-components';
import { FormInput } from '../../FormInput';
import { PhoneFlagDropdown, DEFAULT_PHONE_COUNTRY, phoneCountries } from '../../PhoneFlagDropdown';
import { getCountryCallingCode } from 'react-phone-number-input';
import * as yup from 'yup';

// When the entered email already belongs to a client in the brokerage we do
// NOT block: that client is reused. Instead of auto-filling silently, we surface
// an error under the email field plus an "Import Client Data" button; only when
// the advisor clicks it does the form fill from the existing client and lock
// (the locked/greyed fields are the cue that saved details are in use).
// Error shown under the email field when the typed email matches an existing
// client in the brokerage and the advisor has not imported their details yet.
const EXISTING_EMAIL_ERROR =
  'A client with this email already exists in your brokerage. Click "Import Client Data" to use their saved details.';
const EMAIL_FORMAT_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Client form data
interface ClientFormData {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  phoneCountryCode: string; // ISO alpha-2 for phone (e.g., 'IE', 'GB', 'US')
  dateOfBirth: string;
  gender: string;
  maritalStatus: string;
  crmPersonNumber: string;
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  townCity: string;
  stateProvinceCounty: string;
  postalCode: string;
  countryCode: string;
  country: string;
  businessType: 'business_owner' | 'company_shareholder' | 'none';
}

const GENDER_OPTIONS = [
  { value: 'MALE', text: 'Male' },
  { value: 'FEMALE', text: 'Female' },
];

const MARITAL_STATUS_OPTIONS = [
  { value: 'SINGLE', text: 'Single' },
  { value: 'DIVORCED', text: 'Divorced' },
  { value: 'WIDOWED', text: 'Widowed' },
  { value: 'MARRIED', text: 'Married' },
  { value: 'CIVIL_PARTNERSHIP', text: 'Civil Partnership' },
  { value: 'SEPARATED', text: 'Separated' },
];

const defaultCountry = SUPPORTED_COUNTRIES.find(c => c.value === DEFAULT_COUNTRY);

const createEmptyClientForm = (): ClientFormData => ({
  email: '',
  firstName: '',
  lastName: '',
  phone: `+${getCountryCallingCode(DEFAULT_PHONE_COUNTRY)}`,
  phoneCountryCode: DEFAULT_PHONE_COUNTRY,
  dateOfBirth: '',
  gender: '',
  maritalStatus: '',
  crmPersonNumber: '',
  addressLine1: '',
  addressLine2: '',
  addressLine3: '',
  townCity: '',
  stateProvinceCounty: '',
  postalCode: '',
  countryCode: DEFAULT_COUNTRY,
  country: defaultCountry?.text || 'Ireland',
  businessType: 'none',
});


// Base Yup schema (fields that are always validated)
const clientFormSchema = yup.object({
  email: yup.string()
    .trim()
    .required('Email is required')
    .email('Invalid email format'),
  firstName: yup.string()
    .trim()
    .required('First name is required'),
  lastName: yup.string()
    .trim()
    .required('Last name is required'),
  phone: yup.string()
    .required('Phone is required')
    .transform((value: string) => value?.replaceAll(/\s/g, '') || '')
    .test('phone-min-digits', 'Phone is required', (value) => {
      if (!value || value === '+') return false;
      return value.replace(/^\+/, '').length >= 7;
    })
    .matches(/^\+?[1-9]\d{1,14}$/, 'Invalid phone format (e.g., +353851234567)'),
  dateOfBirth: yup.string()
    .required('Date of birth is required')
    .test('is-past', 'Date of birth must be in the past', (value) => {
      if (!value) return false;
      return new Date(value) < new Date();
    })
    .test('min-age', 'Client must be at least 18 years old', (value) => {
      if (!value) return false;
      const birth = new Date(value);
      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      const m = today.getMonth() - birth.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
      return age >= 18;
    }),
  gender: yup.string()
    .oneOf(['', 'MALE', 'FEMALE'])
    .notRequired(),
  maritalStatus: yup.string()
    .oneOf(['', 'SINGLE', 'DIVORCED', 'WIDOWED', 'MARRIED', 'CIVIL_PARTNERSHIP', 'SEPARATED'])
    .notRequired(),
  addressLine1: yup.string()
    .trim()
    .required('Residential address is required'),
  addressLine2: yup.string().optional().default(''),
  addressLine3: yup.string().optional().default(''),
  townCity: yup.string().optional().default(''),
  postalCode: yup.string().optional().default(''),
  stateProvinceCounty: yup.string().optional().default(''),
  countryCode: yup.string().required(),
  country: yup.string().required(),
  phoneCountryCode: yup.string().required(),
  crmPersonNumber: yup.string().trim().required('CRM person number is required'),
  businessType: yup.string().oneOf(['business_owner', 'company_shareholder', 'none']).required(),
});

function collectYupErrors(data: ClientFormData): Record<string, string> {
  const errors: Record<string, string> = {};
  try {
    clientFormSchema.validateSync(data, { abortEarly: false });
  } catch (err) {
    if (err instanceof yup.ValidationError) {
      for (const inner of err.inner) {
        if (inner.path && !errors[inner.path]) {
          errors[inner.path] = inner.message;
        }
      }
    }
  }
  return errors;
}

function collectAddressErrors(data: ClientFormData): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!data.townCity?.trim()) errors.townCity = 'Town/City is required';
  if (data.postalCode?.trim() && !validatePostalCode(data.countryCode, data.postalCode)) {
    const config = getCountryConfig(data.countryCode);
    errors.postalCode = `Invalid ${config.postalCodeLabel} format. Expected: ${config.postalCodeFormat}`;
  }
  return errors;
}

function findDuplicateEmailError(
  data: ClientFormData,
  otherClients: WizardClient[],
  excludeTempId?: string
): string | null {
  if (!data.email.trim()) return null;
  const existingEmails = otherClients
    .filter(c => c.tempId !== excludeTempId)
    .map(c => c.email.toLowerCase());
  if (existingEmails.includes(data.email.trim().toLowerCase())) {
    return 'This email is already used by another client';
  }
  return null;
}

function validateClientForm(data: ClientFormData, otherClients: WizardClient[], excludeTempId?: string): Record<string, string> {
  const errors = { ...collectYupErrors(data), ...collectAddressErrors(data) };
  if (!errors.email) {
    const dupErr = findDuplicateEmailError(data, otherClients, excludeTempId);
    if (dupErr) errors.email = dupErr;
  }
  return errors;
}

// Lightweight check for button state
function isClientFormValid(data: ClientFormData): boolean {
  if (!clientFormSchema.isValidSync(data)) return false;
  if (!data.townCity?.trim()) return false;
  if (!data.postalCode?.trim()) return false;
  if (!validatePostalCode(data.countryCode, data.postalCode)) return false;
  return true;
}

// Validate a single field for inline feedback
function validateField(data: ClientFormData, field: keyof ClientFormData): string {
  // Address fields validated outside Yup
  if (field === 'townCity' && !data.townCity?.trim()) return 'Town/City is required';
  if (field === 'postalCode') {
    if (!data.postalCode?.trim()) return 'Postal/Zip code is required';
    if (!validatePostalCode(data.countryCode, data.postalCode)) {
      const config = getCountryConfig(data.countryCode);
      return `Invalid ${config.postalCodeLabel} format. Expected: ${config.postalCodeFormat}`;
    }
  }
  // Schema-level field validation
  try {
    clientFormSchema.validateSyncAt(field, data);
  } catch (err) {
    if (err instanceof yup.ValidationError) return err.message;
  }
  return '';
}

function ClientFormFields({
  data,
  errors,
  onChange,
  onBatchChange,
  onEmailBlur,
  label,
  showClientRole = false,
  clientRole = 'primary',
  onClientRoleChange,
  locked = false,
  canImport = false,
  onImportClient,
}: Readonly<{
  data: ClientFormData;
  errors: Record<string, string>;
  onChange: (field: keyof ClientFormData, value: string) => void;
  onBatchChange: (fields: Partial<ClientFormData>) => void;
  onEmailBlur?: () => void;
  label?: string;
  showClientRole?: boolean;
  clientRole?: 'primary' | 'secondary';
  onClientRoleChange?: (role: 'primary' | 'secondary') => void;
  // When true the email matched an existing client and the advisor imported
  // their details: identity/address fields are filled from that client and
  // locked (the email field stays editable so the advisor can correct it). The
  // client's own record is never changed.
  locked?: boolean;
  // When true the typed email matched an existing client but the advisor has
  // not imported their details yet → show the "Import Client Data" button.
  canImport?: boolean;
  onImportClient?: () => void;
}>) {

  const countryConfig = getCountryConfig(data.countryCode);

  const handlePostalCodeChange = (value: string) => {
    const normalized = normalizePostalCode(data.countryCode, value);
    onChange('postalCode', normalized);
  };

  const handleCountryChange = (code: string) => {
    const country = SUPPORTED_COUNTRIES.find(c => c.value === code);
    onChange('countryCode', code);
    onChange('country', country?.text || code);
  };

  const handlePhoneCountrySelect = (alpha2: string, dialCode: string) => {
    onBatchChange({ phoneCountryCode: alpha2, phone: `+${dialCode} ` });
  };

  return (
    <div className="bg-white rounded-card shadow-[0px_0px_10px_0px_rgba(0,0,0,0.1)] px-md py-lg mx-lg">
      {label && (
        <div className="text-black text-h5 font-normal leading-normal font-sans pb-lg">{label}</div>
      )}

      {/* Row 1: First name / Last name */}
      <div className="grid grid-cols-2 gap-6 mb-4">
        <div>
          <InputLabel required>First name</InputLabel>
          <FormInput
            placeholder="Enter first name"
            value={data.firstName}
            onChange={(e) => onChange('firstName', e.target.value)}
            error={errors.firstName}
            disabled={locked}
          />
        </div>
        <div>
          <InputLabel required>Last name</InputLabel>
          <FormInput
            placeholder="Enter last name"
            value={data.lastName}
            onChange={(e) => onChange('lastName', e.target.value)}
            error={errors.lastName}
            disabled={locked}
          />
        </div>
      </div>

      {/* Row 2: Email / Phone */}
      <div className="grid grid-cols-2 gap-6 mb-4">
        <div>
          <InputLabel required>Email address</InputLabel>
          <FormInput
            type="email"
            placeholder="email@example.com"
            value={data.email}
            onChange={(e) => onChange('email', e.target.value)}
            onBlur={onEmailBlur}
            error={errors.email}
            maxLength={320}
          />
          {canImport && !locked && (
            <div className="mt-2">
              <button
                type="button"
                onClick={onImportClient}
                className="inline-flex items-center h-[36px] px-md rounded-lg bg-purple-primary hover:bg-[#220852] text-white text-body-03 font-medium font-sans transition-colors cursor-pointer"
              >
                Import Client Data
              </button>
            </div>
          )}
        </div>
        <div>
          <InputLabel required>Phone number</InputLabel>
          <div className="flex gap-2">
            <PhoneFlagDropdown
              value={data.phoneCountryCode}
              onSelect={handlePhoneCountrySelect}
              disabled={locked}
            />
            <FormInput
              type="tel"
              placeholder="+353 85 234 5678"
              value={data.phone}
              onChange={(e) => onChange('phone', e.target.value)}
              className="flex-1"
              disabled={locked}
            />
          </div>
          {errors.phone && <span className="block mt-sm font-sans text-body-03 font-normal text-[var(--input-error-text)]">{errors.phone}</span>}
        </div>
      </div>

      {/* Row 3: Country / Address line 1 */}
      <div className="grid grid-cols-2 gap-6 mb-4">
        <div>
          <Dropdown
            label="Country of residential address"
            required
            options={SUPPORTED_COUNTRIES.map(c => ({ value: c.value, text: c.text }))}
            value={data.countryCode}
            onChange={(value) => handleCountryChange(value)}
            error={errors.countryCode}
            disabled={locked}
          />
        </div>
        <div>
          <InputLabel required>Address line 1</InputLabel>
          <FormInput
            placeholder="Street address"
            value={data.addressLine1}
            onChange={(e) => onChange('addressLine1', e.target.value)}
            error={errors.addressLine1}
            disabled={locked}
          />
        </div>
      </div>

      {/* Row 4: Address line 2 / Address line 3 */}
      <div className="grid grid-cols-2 gap-6 mb-4">
        <div>
          <InputLabel>Address line 2</InputLabel>
          <FormInput
            placeholder="Apartment, suite, etc."
            value={data.addressLine2}
            onChange={(e) => onChange('addressLine2', e.target.value)}
            disabled={locked}
          />
        </div>
        <div>
          <InputLabel>Address line 3</InputLabel>
          <FormInput
            placeholder="Area, district, etc."
            value={data.addressLine3}
            onChange={(e) => onChange('addressLine3', e.target.value)}
            disabled={locked}
          />
        </div>
      </div>

      {/* Row 5: Town/City / County */}
      <div className="grid grid-cols-2 gap-6 mb-4">
        <div>
          <InputLabel required>{countryConfig.townCityLabel}</InputLabel>
          <FormInput
            placeholder={countryConfig.townCityPlaceholder}
            value={data.townCity}
            onChange={(e) => onChange('townCity', e.target.value)}
            error={errors.townCity}
            disabled={locked}
          />
        </div>
        <div>
          <InputLabel>{countryConfig.stateProvinceLabel}</InputLabel>
          <FormInput
            placeholder={countryConfig.stateProvincePlaceholder}
            value={data.stateProvinceCounty}
            onChange={(e) => onChange('stateProvinceCounty', e.target.value)}
            disabled={locked}
          />
        </div>
      </div>

      {/* Row 6: Postcode / Date of birth */}
      <div className="grid grid-cols-2 gap-6 mb-4">
        <div>
          <InputLabel required>{countryConfig.postalCodeLabel}</InputLabel>
          <FormInput
            placeholder={countryConfig.postalCodePlaceholder}
            value={data.postalCode}
            onChange={(e) => handlePostalCodeChange(e.target.value)}
            error={errors.postalCode}
            disabled={locked}
          />
        </div>
        <DateTimeInput
          label="Date of birth"
          value={data.dateOfBirth}
          onChange={(val) => onChange('dateOfBirth', val)}
          error={errors.dateOfBirth}
          required
          disabled={locked}
        />
      </div>

      {/* Row 7: Gender / Marital Status */}
      <div className="grid grid-cols-2 gap-6 mb-4">
        <Dropdown
          label="Gender"
          options={GENDER_OPTIONS}
          value={data.gender}
          onChange={(value) => onChange('gender', value)}
          error={errors.gender}
          placeholder="Select gender"
          disabled={locked}
        />
        <Dropdown
          label="Marital status"
          options={MARITAL_STATUS_OPTIONS}
          value={data.maritalStatus}
          onChange={(value) => onChange('maritalStatus', value)}
          error={errors.maritalStatus}
          placeholder="Select marital status"
          disabled={locked}
        />
      </div>

      {/* Row 8: CRM person number */}
      <div className="grid grid-cols-2 gap-6 mb-4">
        <div>
          <InputLabel required>CRM person number</InputLabel>
          <FormInput
            placeholder="102931283"
            value={data.crmPersonNumber}
            onChange={(e) => onChange('crmPersonNumber', e.target.value)}
            error={errors.crmPersonNumber}
            disabled={locked}
          />
        </div>
      </div>

      {/* Business owner or company shareholder */}
      <div className="flex items-center justify-between">
        <span className="text-black text-base font-medium leading-normal font-sans">
          Business owner or company shareholder?
        </span>
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 cursor-pointer" onClick={() => onChange('businessType', 'business_owner')}>
            <div className="w-4 h-4 rounded-full border border-purple-primary bg-white flex items-center justify-center flex-shrink-0">
              {data.businessType === 'business_owner' && (
                <div className="w-2 h-2 rounded-full bg-purple-primary" />
              )}
            </div>
            <span className="text-black text-base font-normal leading-normal font-sans">Business owner</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer" onClick={() => onChange('businessType', 'company_shareholder')}>
            <div className="w-4 h-4 rounded-full border border-purple-primary bg-white flex items-center justify-center flex-shrink-0">
              {data.businessType === 'company_shareholder' && (
                <div className="w-2 h-2 rounded-full bg-purple-primary" />
              )}
            </div>
            <span className="text-black text-base font-normal leading-normal font-sans">Company shareholder</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer" onClick={() => onChange('businessType', 'none')}>
            <div className="w-4 h-4 rounded-full border border-purple-primary bg-white flex items-center justify-center flex-shrink-0">
              {data.businessType === 'none' && (
                <div className="w-2 h-2 rounded-full bg-purple-primary" />
              )}
            </div>
            <span className="text-black text-base font-normal leading-normal font-sans">None</span>
          </label>
        </div>
      </div>

      {/* Primary or secondary Client (Joint only) */}
      {showClientRole && (
        <div className="flex items-center justify-between mt-6">
          <span className="text-black text-base font-medium leading-normal font-sans">
            Primary or secondary Client
          </span>
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 cursor-pointer" onClick={() => onClientRoleChange?.('primary')}>
              <div className="w-4 h-4 rounded-full border border-purple-primary bg-white flex items-center justify-center flex-shrink-0">
                {clientRole === 'primary' && (
                  <div className="w-2 h-2 rounded-full bg-purple-primary" />
                )}
              </div>
              <span className="text-black text-base font-normal leading-normal font-sans">Primary client</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer" onClick={() => onClientRoleChange?.('secondary')}>
              <div className="w-4 h-4 rounded-full border border-purple-primary bg-white flex items-center justify-center flex-shrink-0">
                {clientRole === 'secondary' && (
                  <div className="w-2 h-2 rounded-full bg-purple-primary" />
                )}
              </div>
              <span className="text-black text-base font-normal leading-normal font-sans">Secondary client</span>
            </label>
          </div>
        </div>
      )}
    </div>
  );
}

export function CreateRelationshipStep() {
  const {
    state,
    policiesStepEnabled,
    updateProfileData,
    addClient,
    setCreatedProfile,
    setSubmitting,
    setSubmitError,
    clearDraft,
    nextStep,
    setSoloClientFormData,
  } = useWizard();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [primaryForm, setPrimaryForm] = useState<ClientFormData>(createEmptyClientForm());
  const [primaryErrors, setPrimaryErrors] = useState<Record<string, string>>({});
  const [secondaryForm, setSecondaryForm] = useState<ClientFormData>(createEmptyClientForm());
  const [secondaryErrors, setSecondaryErrors] = useState<Record<string, string>>({});
  const primaryEmailCheckTokenRef = useRef(0);
  const secondaryEmailCheckTokenRef = useRef(0);
  // Whether each form is locked onto an existing client (imported + read-only).
  const [primaryLocked, setPrimaryLocked] = useState(false);
  const [secondaryLocked, setSecondaryLocked] = useState(false);
  const primaryLockedRef = useRef(false);
  const secondaryLockedRef = useRef(false);
  // The email that was imported into each locked form. Re-blurring that exact
  // email is a no-op so the imported data is not wiped.
  const primaryLockedEmailRef = useRef('');
  const secondaryLockedEmailRef = useRef('');
  // A detected-but-not-yet-imported existing client for each form. When set, the
  // email field shows EXISTING_EMAIL_ERROR and an "Import Client Data" button.
  const [primaryExistingMatch, setPrimaryExistingMatch] = useState<EmailAvailability | null>(null);
  const [secondaryExistingMatch, setSecondaryExistingMatch] = useState<EmailAvailability | null>(null);
  // Single source of truth for the Primary/Secondary Client toggle (Joint only):
  // which container (1 = Client 1, 2 = Client 2) holds the Primary client.
  const [primaryContainer, setPrimaryContainer] = useState<1 | 2>(1);
  // Joint only: defer navigation until the wizard state reflects the created
  // profile + both clients (addClient updates are batched, so calling
  // nextStep() inline reads stale state and the step-1 guard blocks it).
  const [pendingNav, setPendingNav] = useState(false);

  const isJoint = state.profileData.profileType === 'JOINT';

  // Load existing client data if we come back to step 1
  useEffect(() => {
    if (state.clients.length > 0) {
      // Keep container identity stable (Client 1 = clients[0], Client 2 = clients[1]);
      // restore the Primary/Secondary toggle from the persisted isPrimary flag.
      const container1 = state.clients[0];
      setPrimaryForm(clientToForm(container1));

      if (isJoint && state.clients.length >= 2) {
        setSecondaryForm(clientToForm(state.clients[1]));
        setPrimaryContainer(container1.isPrimary ? 1 : 2);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Joint only: once the just-created profile + both clients are reflected in
  // wizard state, advance to step 2. This makes the Create button a single
  // click (the step-1 navigation guard only validates true after the flush).
  useEffect(() => {
    if (pendingNav && state.createdProfileId && state.clients.length === 2) {
      setPendingNav(false);
      nextStep();
    }
  }, [pendingNav, state.createdProfileId, state.clients.length, nextStep]);

  // Reactively flag a duplicate email on Client 2 (Joint) so the user gets
  // feedback in the field without needing to submit (the Create button stays
  // disabled while the emails match, so submit is unreachable otherwise).
  useEffect(() => {
    if (!isJoint) return;
    const DUP_MSG = 'Email must be different from Client 1';
    const isDuplicate =
      !!secondaryForm.email.trim() &&
      !!primaryForm.email.trim() &&
      secondaryForm.email.trim().toLowerCase() === primaryForm.email.trim().toLowerCase();
    setSecondaryErrors(prev => {
      if (isDuplicate) {
        // Only surface the duplicate hint; never override a real
        // format/required error already produced by the change handler.
        if (prev.email) return prev;
        return { ...prev, email: DUP_MSG };
      }
      // Not a duplicate: clear ONLY our own duplicate message, leaving any
      // genuine field error (and pristine empty fields) untouched.
      if (prev.email === DUP_MSG) return { ...prev, email: '' };
      return prev;
    });
  }, [isJoint, primaryForm.email, secondaryForm.email]);

  // Track Solo form data for draft auto-save validation + inline validation
  const handlePrimaryChange = useCallback((field: keyof ClientFormData, value: string) => {
    // While locked onto an imported client, the email is the only editable
    // field. If the advisor edits it away from that client's email they no
    // longer want the imported client: unlock and clear the stale details so
    // they enter a fresh client (a later blur re-checks the new email).
    if (field === 'email' && primaryLockedRef.current && value.trim() !== primaryLockedEmailRef.current) {
      primaryLockedRef.current = false;
      primaryLockedEmailRef.current = '';
      setPrimaryLocked(false);
      setPrimaryExistingMatch(null);
      const fresh = { ...createEmptyClientForm(), email: value };
      setPrimaryForm(fresh);
      setPrimaryErrors({ email: validateField(fresh, 'email') });
      if (state.profileData.profileType === 'SOLO') {
        setSoloClientFormData(!!fresh.email);
      }
      return;
    }
    setPrimaryForm(prev => {
      const updated = { ...prev, [field]: value };
      if (state.profileData.profileType === 'SOLO') {
        const hasData = !!(updated.email || updated.firstName || updated.lastName);
        setSoloClientFormData(hasData);
      }
      // Inline validation: validate the changed field
      const error = validateField(updated, field);
      setPrimaryErrors(prevErrors => ({ ...prevErrors, [field]: error }));
      return updated;
    });
  }, [state.profileData.profileType, setSoloClientFormData]);

  const handlePrimaryBatchChange = useCallback((fields: Partial<ClientFormData>) => {
    setPrimaryForm(prev => {
      const updated = { ...prev, ...fields };
      if (state.profileData.profileType === 'SOLO') {
        const hasData = !!(updated.email || updated.firstName || updated.lastName);
        setSoloClientFormData(hasData);
      }
      // Inline validation for each changed field
      const newErrors: Record<string, string> = {};
      for (const field of Object.keys(fields) as (keyof ClientFormData)[]) {
        newErrors[field] = validateField(updated, field);
      }
      setPrimaryErrors(prevErrors => ({ ...prevErrors, ...newErrors }));
      return updated;
    });
  }, [state.profileData.profileType, setSoloClientFormData]);

  const handleSecondaryChange = useCallback((field: keyof ClientFormData, value: string) => {
    // Same as primary: editing the email away from the imported client unlocks
    // and clears Client 2's stale imported details.
    if (field === 'email' && secondaryLockedRef.current && value.trim() !== secondaryLockedEmailRef.current) {
      secondaryLockedRef.current = false;
      secondaryLockedEmailRef.current = '';
      setSecondaryLocked(false);
      setSecondaryExistingMatch(null);
      const fresh = { ...createEmptyClientForm(), email: value };
      setSecondaryForm(fresh);
      setSecondaryErrors({ email: validateField(fresh, 'email') });
      return;
    }
    setSecondaryForm(prev => {
      const updated = { ...prev, [field]: value };
      const error = validateField(updated, field);
      setSecondaryErrors(prevErrors => ({ ...prevErrors, [field]: error }));
      return updated;
    });
  }, []);

  const handleSecondaryBatchChange = useCallback((fields: Partial<ClientFormData>) => {
    setSecondaryForm(prev => {
      const updated = { ...prev, ...fields };
      const newErrors: Record<string, string> = {};
      for (const field of Object.keys(fields) as (keyof ClientFormData)[]) {
        newErrors[field] = validateField(updated, field);
      }
      setSecondaryErrors(prevErrors => ({ ...prevErrors, ...newErrors }));
      return updated;
    });
  }, []);

  // On email blur, check whether the email already belongs to a client in the
  // brokerage. If so, do NOT auto-fill: record the match (which surfaces an
  // error under the email field plus an "Import Client Data" button) so the
  // advisor can opt in. If the email is free, clear any pending match and — if
  // the form was previously locked onto an imported client — reset it (keeping
  // the typed email) so the advisor can enter a fresh client.
  const checkBrokerageEmail = useCallback(async (
    email: string,
    setForm: React.Dispatch<React.SetStateAction<ClientFormData>>,
    setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>,
    setLocked: React.Dispatch<React.SetStateAction<boolean>>,
    lockedRef: React.MutableRefObject<boolean>,
    lockedEmailRef: React.MutableRefObject<string>,
    setExistingMatch: React.Dispatch<React.SetStateAction<EmailAvailability | null>>,
    tokenRef: React.MutableRefObject<number>,
  ) => {
    const trimmed = email.trim();
    if (!trimmed || !EMAIL_FORMAT_REGEX.test(trimmed)) {
      return;
    }
    // Already imported this exact client → nothing to do (don't wipe the import).
    if (lockedRef.current && lockedEmailRef.current === trimmed) {
      return;
    }
    const token = ++tokenRef.current;
    try {
      const availability = await clientApi.checkEmailExists(trimmed);
      if (token !== tokenRef.current) {
        return;
      }
      if (availability.exists) {
        // Offer the import instead of performing it.
        setExistingMatch(availability);
        setErrors(prev => ({ ...prev, email: EXISTING_EMAIL_ERROR }));
        if (lockedRef.current) {
          // Was locked onto a different imported client → reset to a fresh,
          // editable form (keeping the typed email) until the advisor imports.
          setForm(() => ({ ...createEmptyClientForm(), email: trimmed }));
          lockedRef.current = false;
          lockedEmailRef.current = '';
          setLocked(false);
        }
      } else {
        setExistingMatch(null);
        // Clear only our own existing-email error; leave genuine field errors.
        setErrors(prev => (prev.email === EXISTING_EMAIL_ERROR ? { ...prev, email: '' } : prev));
        if (lockedRef.current) {
          // Was locked onto a now-changed email → reset to a fresh, editable form.
          setForm(() => ({ ...createEmptyClientForm(), email: trimmed }));
          lockedRef.current = false;
          lockedEmailRef.current = '';
          setLocked(false);
        }
      }
    } catch (err) {
      console.warn('Email lookup failed; deferring to backend reuse/backstop', err);
    }
  }, []);

  // Import the detected existing client into the form and lock it (read-only).
  // The email field stays editable so a typo can still be corrected.
  const importExistingClient = useCallback((
    match: EmailAvailability | null,
    typedEmail: string,
    setForm: React.Dispatch<React.SetStateAction<ClientFormData>>,
    setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>,
    setLocked: React.Dispatch<React.SetStateAction<boolean>>,
    lockedRef: React.MutableRefObject<boolean>,
    lockedEmailRef: React.MutableRefObject<string>,
    setExistingMatch: React.Dispatch<React.SetStateAction<EmailAvailability | null>>,
  ) => {
    if (!match) return;
    const importedEmail = match.email || typedEmail.trim();
    setForm(() => ({ ...existingToClientForm(match), email: importedEmail }));
    setErrors({});
    lockedRef.current = true;
    lockedEmailRef.current = importedEmail;
    setLocked(true);
    setExistingMatch(null);
  }, []);

  const handlePrimaryEmailBlur = useCallback(() => {
    void checkBrokerageEmail(primaryForm.email, setPrimaryForm, setPrimaryErrors, setPrimaryLocked, primaryLockedRef, primaryLockedEmailRef, setPrimaryExistingMatch, primaryEmailCheckTokenRef);
  }, [primaryForm.email, checkBrokerageEmail]);

  const handleSecondaryEmailBlur = useCallback(() => {
    void checkBrokerageEmail(secondaryForm.email, setSecondaryForm, setSecondaryErrors, setSecondaryLocked, secondaryLockedRef, secondaryLockedEmailRef, setSecondaryExistingMatch, secondaryEmailCheckTokenRef);
  }, [secondaryForm.email, checkBrokerageEmail]);

  const handlePrimaryImport = useCallback(() => {
    importExistingClient(primaryExistingMatch, primaryForm.email, setPrimaryForm, setPrimaryErrors, setPrimaryLocked, primaryLockedRef, primaryLockedEmailRef, setPrimaryExistingMatch);
  }, [primaryExistingMatch, primaryForm.email, importExistingClient]);

  const handleSecondaryImport = useCallback(() => {
    importExistingClient(secondaryExistingMatch, secondaryForm.email, setSecondaryForm, setSecondaryErrors, setSecondaryLocked, secondaryLockedRef, secondaryLockedEmailRef, setSecondaryExistingMatch);
  }, [secondaryExistingMatch, secondaryForm.email, importExistingClient]);

  // Single-step wizard (Add Policies step off): after the relationship is
  // created, hand over to the send-invitations page with the "Add your
  // policies" email template — the same screen the legacy step-2 "I want the
  // client to input the policy details" choice led to. The advisor sends the
  // email from there, or cancels to land on the relationship detail page.
  const showInvitationStep = useCallback((
    profileId: string,
    profileName: string,
    clientCount: number,
  ) => {
    showToast(
      <>
        <span className="font-bold">Relationship {profileName}</span>
        {' '}has been created with{' '}
        <span className="font-bold">{clientCount} client{clientCount === 1 ? '' : 's'}</span>
        . You can now send invitation emails to your client{clientCount === 1 ? '' : 's'}.
      </>
    );
    navigate(`/relationships/new/send-invitations?profileId=${profileId}&templateType=add-policies`);
  }, [navigate, showToast]);

  const isFormComplete = useMemo(() => {
    // A detected-but-not-yet-imported existing client blocks submit: the advisor
    // must import their details (or change the email) before creating.
    if (primaryExistingMatch) return false;
    if (!isClientFormValid(primaryForm)) return false;
    if (isJoint) {
      if (secondaryExistingMatch) return false;
      if (!isClientFormValid(secondaryForm)) return false;
      // Two clients in the SAME relationship still can't share an email.
      if (primaryForm.email.trim().toLowerCase() === secondaryForm.email.trim().toLowerCase() && primaryForm.email.trim()) return false;
    }
    return true;
  }, [primaryForm, secondaryForm, isJoint, primaryExistingMatch, secondaryExistingMatch]);

  const handleSubmit = async () => {
    // Validate primary client
    const pErrors = validateClientForm(primaryForm, []);
    setPrimaryErrors(pErrors);

    // Validate secondary client for Joint
    let sErrors: Record<string, string> = {};
    if (isJoint) {
      sErrors = validateClientForm(secondaryForm, []);
      // Check duplicate email
      if (primaryForm.email.trim().toLowerCase() === secondaryForm.email.trim().toLowerCase() && primaryForm.email.trim()) {
        sErrors.email = 'Email must be different from Client 1';
      }
      setSecondaryErrors(sErrors);
    }

    if (Object.keys(pErrors).length > 0 || Object.keys(sErrors).length > 0) return;

    // Build wizard clients for profile name derivation.
    // isPrimary follows the Primary/Secondary toggle for Joint; Solo is always primary.
    const container1IsPrimary = !isJoint || primaryContainer === 1;
    const clients: WizardClient[] = [formToWizardClient(primaryForm, container1IsPrimary)];
    if (isJoint) {
      clients.push(formToWizardClient(secondaryForm, !container1IsPrimary));
    }

    const profileName = deriveProfileName(state.profileData.profileType, clients);

    setSubmitting(true);
    setSubmitError(null);

    try {
      const bulkData: RelationshipBulkCreateDTO = {
        profileName,
        profileType: state.profileData.profileType,
        crmRelationshipNumber: state.profileData.crmRelationshipNumber || undefined,
        clients: clients.map(c => ({
          email: c.email,
          firstName: c.firstName,
          lastName: c.lastName,
          phone: c.phone.replaceAll(/\s/g, ''),
          dateOfBirth: c.dateOfBirth,
          crmPersonNumber: c.crmPersonNumber || undefined,
          gender: c.gender || undefined,
          maritalStatus: c.maritalStatus || undefined,
          addressLine1: c.addressLine1,
          addressLine2: c.addressLine2 || undefined,
          addressLine3: c.addressLine3 || undefined,
          townCity: c.townCity,
          stateProvinceCounty: c.stateProvinceCounty || undefined,
          postalCode: c.postalCode,
          countryCode: c.countryCode,
          country: c.country,
          isPrimary: c.isPrimary,
          tempId: c.tempId,
        })),
        policies: [],
        sendInvitations: false,
      };

      console.log('[CreateRelationship] Submit payload:', JSON.stringify(bulkData, null, 2));
      const response = await relationshipBulkApi.createRelationshipBulk(bulkData);

      // Store created profile data
      // Add clients to wizard context (with real data). addClient() mints a
      // FRESH tempId, so we must re-key the backend's tempId->realId map onto
      // those new tempIds — otherwise state.clients[].tempId no longer matches
      // state.createdClientIds keys and JOINT signatory resolution in
      // AddPoliciesStep (buildJointSignatories) silently produces zero
      // signatories ("No signatories" / nothing to sign for the client).
      const backendIdMap = response.idMappings.clients; // originalTempId -> realId
      const remappedClientIds: Record<string, string> = {};
      clients.forEach(({ tempId: originalTempId, ...clientWithoutTempId }) => {
        const newTempId = addClient(clientWithoutTempId);
        const realId = backendIdMap[originalTempId];
        if (realId) remappedClientIds[newTempId] = realId;
      });

      setCreatedProfile(response.profile.id, remappedClientIds);

      clearDraft();
      setSubmitting(false);

      // Clients flagged as a business owner / company shareholder trigger the
      // Corporate Holdings step (company-registry search per flagged client),
      // reached after creation because the search/confirm are keyed to the now-
      // created client + relationship ids. `clients[i]` corresponds to `forms[i]`
      // and still carries its ORIGINAL tempId, so it maps through backendIdMap.
      const forms = isJoint ? [primaryForm, secondaryForm] : [primaryForm];
      const flaggedClients = clients
        .map((c, i) => ({ form: forms[i], realId: backendIdMap[c.tempId] }))
        .filter((x) => x.form && x.form.businessType !== 'none' && x.realId)
        .map((x) => ({
          clientProfileId: x.realId,
          firstName: x.form.firstName,
          lastName: x.form.lastName,
          birthYear: x.form.dateOfBirth ? x.form.dateOfBirth.slice(0, 4) : undefined,
          address: x.form.addressLine1 || x.form.townCity || undefined,
          businessType: x.form.businessType as 'business_owner' | 'company_shareholder',
        }));

      if (!policiesStepEnabled) {
        if (flaggedClients.length > 0) {
          // Detour through the Corporate Holdings step before invitations.
          showToast(
            <>
              <span className="font-bold">Relationship {profileName}</span>
              {' '}has been created. Search the company registry for the flagged{' '}
              client{flaggedClients.length === 1 ? '' : 's'}.
            </>
          );
          navigate(`/relationships/new/corporate-holdings?profileId=${response.profile.id}`,
            { state: { flaggedClients } });
        } else {
          // Single-step wizard: hand over to the add-policies invitation page.
          showInvitationStep(response.profile.id, profileName, clients.length);
        }
      } else if (isJoint) {
        // Navigate to step 2.
        // Joint: defer until wizard state reflects the 2 added clients (avoids
        // the stale-state guard that required a second click). Solo: unchanged.
        setPendingNav(true);
      } else {
        nextStep();
      }
    } catch (err: unknown) {
      console.error('Failed to create relationship:', err);
      if (err && typeof err === 'object' && 'response' in err) {
        const axErr = err as { response?: { status?: number; data?: unknown } };
        console.error('[CreateRelationship] Response status:', axErr.response?.status);
        console.error('[CreateRelationship] Response data:', JSON.stringify(axErr.response?.data, null, 2));
      }
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string; validationErrors?: ValidationErrorsDTO } } };
        const validationErrors = axiosError.response?.data?.validationErrors;

        // Map field-level client errors back to the correct form
        if (validationErrors?.clients) {
          const clientErrors = validationErrors.clients;
          // Match client errors by tempId to primary/secondary forms
          for (const [tempId, fieldErrors] of Object.entries(clientErrors)) {
            const matchesPrimary = clients.find(c => c.isPrimary && c.tempId === tempId);
            const matchesSecondary = clients.find(c => !c.isPrimary && c.tempId === tempId);
            if (matchesPrimary) {
              setPrimaryErrors(prev => ({ ...prev, ...fieldErrors }));
            } else if (matchesSecondary) {
              setSecondaryErrors(prev => ({ ...prev, ...fieldErrors }));
            }
          }
        }

        // Show global errors in the banner
        const globalErrors = validationErrors?.global;
        if (globalErrors && globalErrors.length > 0) {
          setSubmitError(globalErrors.join('\n'));
        } else if (!validationErrors?.clients || Object.keys(validationErrors.clients).length === 0) {
          setSubmitError(axiosError.response?.data?.message || 'Failed to create relationship. Please try again.');
        }
      } else if (err instanceof Error) {
        setSubmitError(err.message);
      } else {
        setSubmitError('Failed to create relationship. Please try again.');
      }
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 pt-lg bg-page-bg flex-1">
      {/* Card 1: Relationship Details */}
      <div className="bg-white rounded-card shadow-[0px_0px_10px_0px_rgba(0,0,0,0.1)] px-md py-lg mx-lg">
        <div className="text-black text-h5 font-normal leading-normal font-sans pb-lg">Relationship details</div>

        <div className="grid grid-cols-2 gap-6 items-end">
          <div>
            <Dropdown
              label="Relationship type"
              required
              options={[
                { value: 'SOLO', text: 'Solo' },
                { value: 'JOINT', text: 'Joint' },
              ]}
              value={state.profileData.profileType || ''}
              onChange={(value) => updateProfileData({ profileType: value as RelationshipType })}
            />
          </div>
          <div>
            <InputLabel>CRM relationship number</InputLabel>
            <FormInput
              placeholder="#11231231092"
              value={state.profileData.crmRelationshipNumber || ''}
              onChange={(e) => updateProfileData({ crmRelationshipNumber: e.target.value })}
              maxLength={100}
            />
          </div>
        </div>
      </div>

      <ClientFormFields
        data={primaryForm}
        errors={primaryErrors}
        onChange={handlePrimaryChange}
        onBatchChange={handlePrimaryBatchChange}
        onEmailBlur={handlePrimaryEmailBlur}
        label={isJoint ? 'Client 1 details' : 'Add client details'}
        showClientRole={isJoint}
        clientRole={primaryContainer === 1 ? 'primary' : 'secondary'}
        onClientRoleChange={(role) => setPrimaryContainer(role === 'primary' ? 1 : 2)}
        locked={primaryLocked}
        canImport={!!primaryExistingMatch}
        onImportClient={handlePrimaryImport}
      />


      {isJoint && (
        <ClientFormFields
          data={secondaryForm}
          errors={secondaryErrors}
          onChange={handleSecondaryChange}
          onBatchChange={handleSecondaryBatchChange}
          onEmailBlur={handleSecondaryEmailBlur}
          label="Client 2 details"
          showClientRole={isJoint}
          clientRole={primaryContainer === 2 ? 'primary' : 'secondary'}
          onClientRoleChange={(role) => setPrimaryContainer(role === 'primary' ? 2 : 1)}
          locked={secondaryLocked}
          canImport={!!secondaryExistingMatch}
          onImportClient={handleSecondaryImport}
        />
      )}

      {/* Error display */}
      {state.submitError && (
        <div className="bg-red-50 border border-red-200 rounded-button p-4">
          <p className="text-sm text-red-700 whitespace-pre-line">{state.submitError}</p>
        </div>
      )}

      {/* Submit button */}
      <div className="flex justify-end pr-lg pb-lg">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={state.isSubmitting || !isFormComplete}
          className={`flex h-[48px] px-xl py-[10px] justify-center items-center gap-sm shrink-0 rounded-button transition-colors ${
            state.isSubmitting || !isFormComplete
              ? 'bg-[#E1E1E1] cursor-not-allowed'
              : 'bg-purple-primary hover:bg-[#220852] cursor-pointer'
          }`}
        >
          {state.isSubmitting ? (
            <div className="w-5 h-5 border-2 border-grey-01 border-t-transparent rounded-full animate-spin" />
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clipPath="url(#clip0_btn_icon)">
                <mask id="mask0_btn_icon" style={{ maskType: 'luminance' }} maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
                  <path d="M23.5 0.5V23.5H0.5V0.5H23.5Z" fill="white" stroke="white"/>
                </mask>
                <g mask="url(#mask0_btn_icon)">
                  <path d="M16 12.75H8C7.59 12.75 7.25 12.41 7.25 12C7.25 11.59 7.59 11.25 8 11.25H16C16.41 11.25 16.75 11.59 16.75 12C16.75 12.41 16.41 12.75 16 12.75Z" fill={state.isSubmitting || !isFormComplete ? '#333333' : 'white'}/>
                  <path d="M12 16.75C11.59 16.75 11.25 16.41 11.25 16V8C11.25 7.59 11.59 7.25 12 7.25C12.41 7.25 12.75 7.59 12.75 8V16C12.75 16.41 12.41 16.75 12 16.75Z" fill={state.isSubmitting || !isFormComplete ? '#333333' : 'white'}/>
                  <path d="M15 22.75H9C3.57 22.75 1.25 20.43 1.25 15V9C1.25 3.57 3.57 1.25 9 1.25H15C20.43 1.25 22.75 3.57 22.75 9V15C22.75 20.43 20.43 22.75 15 22.75ZM9 2.75C4.39 2.75 2.75 4.39 2.75 9V15C2.75 19.61 4.39 21.25 9 21.25H15C19.61 21.25 21.25 19.61 21.25 15V9C21.25 4.39 19.61 2.75 15 2.75H9Z" fill={state.isSubmitting || !isFormComplete ? '#333333' : 'white'}/>
                </g>
              </g>
              <defs>
                <clipPath id="clip0_btn_icon">
                  <rect width="24" height="24" fill="white"/>
                </clipPath>
              </defs>
            </svg>
          )}
          <span
            className={`font-sans text-body-02 font-medium capitalize ${
              state.isSubmitting || !isFormComplete ? 'text-grey-01' : 'text-white'
            }`}
          >
            Create Relationship
          </span>
        </button>
      </div>
    </div>
  );
}

// Helpers
function clientToForm(client: WizardClient): ClientFormData {
  // Derive phone country from the phone number's dial code, default to IE
  let phoneCC = DEFAULT_PHONE_COUNTRY;
  if (client.phone) {
    for (const cc of phoneCountries) {
      const dialCode = `+${getCountryCallingCode(cc)}`;
      if (client.phone.startsWith(dialCode)) {
        phoneCC = cc;
        break;
      }
    }
  }

  return {
    email: client.email,
    firstName: client.firstName,
    lastName: client.lastName,
    phone: client.phone,
    phoneCountryCode: phoneCC,
    dateOfBirth: client.dateOfBirth,
    gender: client.gender || '',
    maritalStatus: client.maritalStatus || '',
    crmPersonNumber: client.crmPersonNumber || '',
    addressLine1: client.addressLine1,
    addressLine2: client.addressLine2 || '',
    addressLine3: client.addressLine3 || '',
    townCity: client.townCity,
    stateProvinceCounty: client.stateProvinceCounty || '',
    postalCode: client.postalCode,
    countryCode: client.countryCode,
    country: client.country,
    businessType: 'none',
  };
}

// Map the existing-client lookup payload into the client form, deriving the
// phone country from the dial code (mirrors clientToForm). Used to auto-fill +
// lock the form when an entered email matches an existing client.
function existingToClientForm(a: EmailAvailability): ClientFormData {
  const phone = a.phone || `+${getCountryCallingCode(DEFAULT_PHONE_COUNTRY)}`;
  let phoneCC = DEFAULT_PHONE_COUNTRY;
  if (a.phone) {
    for (const cc of phoneCountries) {
      const dialCode = `+${getCountryCallingCode(cc)}`;
      if (a.phone.startsWith(dialCode)) {
        phoneCC = cc;
        break;
      }
    }
  }
  const countryCode = a.countryCode || DEFAULT_COUNTRY;
  const country = a.country || SUPPORTED_COUNTRIES.find(c => c.value === countryCode)?.text || 'Ireland';
  return {
    email: a.email || '',
    firstName: a.firstName || '',
    lastName: a.lastName || '',
    phone,
    phoneCountryCode: phoneCC,
    dateOfBirth: a.dateOfBirth || '',
    gender: a.gender || '',
    maritalStatus: a.maritalStatus || '',
    crmPersonNumber: a.crmPersonNumber || '',
    addressLine1: a.addressLine1 || '',
    addressLine2: a.addressLine2 || '',
    addressLine3: a.addressLine3 || '',
    townCity: a.townCity || '',
    stateProvinceCounty: a.stateProvinceCounty || '',
    postalCode: a.postalCode || '',
    countryCode,
    country,
    businessType: 'none',
  };
}

function formToWizardClient(form: ClientFormData, isPrimary: boolean): WizardClient {
  return {
    tempId: `temp_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
    email: form.email.trim(),
    firstName: form.firstName.trim(),
    lastName: form.lastName.trim(),
    phone: form.phone.trim(),
    dateOfBirth: form.dateOfBirth,
    gender: form.gender as 'MALE' | 'FEMALE',
    maritalStatus: form.maritalStatus as 'SINGLE' | 'DIVORCED' | 'WIDOWED' | 'MARRIED' | 'CIVIL_PARTNERSHIP' | 'SEPARATED',
    crmPersonNumber: form.crmPersonNumber.trim() || undefined,
    addressLine1: form.addressLine1.trim(),
    addressLine2: form.addressLine2.trim() || undefined,
    addressLine3: form.addressLine3.trim() || undefined,
    townCity: form.townCity.trim(),
    stateProvinceCounty: form.stateProvinceCounty.trim() || undefined,
    postalCode: form.postalCode.trim(),
    countryCode: form.countryCode,
    country: form.country,
    isPrimary,
    status: 'prospect',
  };
}
